import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

import { useEffect } from "react";
import { useLenis } from "@/components/smooth-scroll";
import { useLanguage } from "@/context/language-context";
import { CodeXml, ExternalLink } from "lucide-react";

interface ProjectItem {
  id: string;
  title: string;
  category: string;
  year: string;
  description: string;
  image: string;
  demo?: string;
  repo?: string;
  stack?: string[];
}

interface ProjectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: ProjectItem | null;
}

export function ProjectModal({ open, onOpenChange, project }: ProjectModalProps) {
  const lenis = useLenis();
  const { content } = useLanguage();

  useEffect(() => {
    if (open) {
      lenis?.stop();
    } else {
      lenis?.start();
    }
  }, [open, lenis]);

  if (!project) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={true}
        className="flex max-h-[90vh] w-[95vw] shrink-0 flex-col gap-0 border-border/50 bg-background/95 p-0 backdrop-blur-xl sm:max-w-[800px]"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{project.title}</DialogTitle>
          <DialogDescription>
            {content?.others?.project_details || "Details about"} {project.title}
          </DialogDescription>
        </DialogHeader>

        <div className="absolute top-0 right-0 left-0 z-10 h-px bg-linear-to-r from-transparent via-primary/50 to-transparent" />

        <div className="h-full w-full flex-1 overflow-y-auto" data-lenis-prevent="true">
          <div className="relative h-[40vh] w-full shrink-0 sm:h-[50vh]">
            {project.image && (
              <img
                src={project.image}
                alt={project.title}
                className="absolute inset-0 h-full w-full rounded-lg object-cover object-top"
              />
            )}
            <div className="absolute inset-0 bg-linear-to-t from-background to-transparent" />

            <div className="absolute right-6 bottom-6 left-6 flex flex-col justify-between gap-4 sm:right-10 sm:bottom-10 sm:left-10 sm:flex-row sm:items-end">
              <div>
                <h2 className="mb-2 text-4xl font-bold tracking-tighter text-foreground sm:text-6xl md:text-7xl">
                  {project.title}
                </h2>
                <div className="flex items-center gap-3 font-mono text-sm tracking-widest text-muted-foreground uppercase">
                  <span>{project.category}</span>
                  <span className="h-1 w-1 rounded-full bg-border" />
                  <span>{project.year}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-10 p-6 sm:p-10">
            <div>
              <h3 className="mb-4 text-sm tracking-widest text-muted-foreground uppercase">
                {content?.others?.about_project || "About the Project"}
              </h3>
              <p className="text-lg leading-relaxed font-light text-foreground/80">
                {project.description}
              </p>
            </div>

            {project.stack && project.stack.length > 0 && (
              <div>
                <h3 className="mb-4 text-sm tracking-widest text-muted-foreground uppercase">
                  {content?.others?.technologies || "Technologies"}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {project.stack.map((tech) => (
                    <span
                      key={tech}
                      className="rounded-full border border-border/50 bg-secondary/50 px-4 py-1.5 text-sm"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {(project.demo || project.repo) && (
              <div className="flex flex-wrap gap-4 border-t border-border/50 pt-4">
                {project.demo && (
                  <a
                    href={project.demo}
                    target="_blank"
                    rel="noopener"
                    className="group relative flex h-12 cursor-pointer items-center justify-center overflow-hidden rounded-full border border-border/50 bg-foreground px-6 text-background shadow-lg transition-all duration-500 ease-out hover:-translate-y-1 hover:border-foreground/30 hover:bg-background hover:text-foreground sm:px-8"
                  >
                    <div className="absolute inset-0 flex h-full w-full -translate-x-full -skew-x-13 justify-center group-hover:translate-x-full group-hover:duration-1000">
                      <div className="relative h-full w-8 bg-background/20 dark:bg-foreground/10" />
                    </div>
                    <span className="relative z-10 flex items-center gap-2 text-xs font-medium tracking-widest uppercase sm:text-sm">
                      {content?.others?.live_demo || "See Live"}
                      <ExternalLink className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1 sm:h-4 sm:w-4" />
                    </span>
                  </a>
                )}

                {project.repo && (
                  <a
                    href={project.repo}
                    target="_blank"
                    rel="noopener"
                    className="group relative flex h-12 cursor-pointer items-center justify-center overflow-hidden rounded-full border border-border/50 bg-secondary/10 px-6 text-foreground shadow-sm transition-all duration-500 ease-out hover:-translate-y-1 hover:border-foreground/30 hover:bg-foreground hover:text-background sm:px-8"
                  >
                    <div className="absolute inset-0 flex h-full w-full -translate-x-full -skew-x-13 justify-center group-hover:translate-x-full group-hover:duration-1000">
                      <div className="relative h-full w-8 bg-foreground/10 dark:bg-background/20" />
                    </div>
                    <span className="relative z-10 flex items-center gap-2 text-xs font-medium tracking-widest uppercase sm:text-sm">
                      {content?.others?.source_code || "Source Code"}
                      <CodeXml className="h-3.5 w-3.5 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-12 sm:h-4 sm:w-4" />
                    </span>
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="absolute right-0 bottom-0 left-0 z-10 h-px bg-linear-to-r from-transparent via-primary/50 to-transparent" />
      </DialogContent>
    </Dialog>
  );
}
